import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useCallback } from 'react';
import api from '../services/api';
import type {
  Pipeline,
  DefendantsResponse,
  ContactsSearchResponse,
  ContactSearchResult,
  CreateCasePayload,
  CreateCaseResponse,
  CreateContactPayload,
  CreateContactResponse,
} from '../types/newRecord';

/* ─────────────────────────────────────────────
 * 1. Pipelines (case types + stages) — cached
 * ───────────────────────────────────────────── */

export function usePipelines() {
  return useQuery<Pipeline[]>({
    queryKey: ['pipelines'],
    queryFn: async () => {
      const { data } = await api.get('/api/admin/pipelines');
      // Response shape: { success, data: Pipeline[] } or just Pipeline[]
      return (data.data ?? data.pipelines ?? data) as Pipeline[];
    },
    staleTime: 5 * 60_000, // pipelines rarely change
  });
}

/* ─────────────────────────────────────────────
 * 2. Defendant (lender) async search
 * ───────────────────────────────────────────── */

export function useDefendantSearch() {
  const [results, setResults] = useState<{ value: string; label: string }[]>([]);
  const [loading, setLoading] = useState(false);

  const search = useCallback(async (term: string) => {
    if (term.length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get<DefendantsResponse>('/api/defendants', {
        params: { search: term, page: 1 },
      });
      const defendants = data.defendants ?? [];
      setResults(
        defendants.map((d) => ({
          value: d.defendant_name,
          label: d.defendant_name,
        })),
      );
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  return { results, loading, search, setResults };
}

/* ─────────────────────────────────────────────
 * 3. Contact async search
 * ───────────────────────────────────────────── */

export function useContactSearch() {
  const [results, setResults] = useState<{ value: string; label: string }[]>([]);
  const [loading, setLoading] = useState(false);

  const search = useCallback(async (term: string) => {
    if (term.length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get<ContactsSearchResponse>('/api/contacts', {
        params: { q: term, limit: 20 },
      });
      const contacts: ContactSearchResult[] =
        data.results ?? (data as unknown as ContactSearchResult[]);
      setResults(
        contacts.map((c) => {
          const name = c.name ?? `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim();
          const id = c.ContactID ?? c.id ?? '';
          return { value: String(id), label: `${name} (${id})` };
        }),
      );
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  return { results, loading, search, setResults };
}

/* ─────────────────────────────────────────────
 * 4. Create case mutation
 * ───────────────────────────────────────────── */

export function useCreateCase() {
  const qc = useQueryClient();
  return useMutation<CreateCaseResponse, Error, CreateCasePayload>({
    mutationFn: async (payload) => {
      const { data } = await api.post('/api/casefile', payload);
      return data as CreateCaseResponse;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['home'] }); // refresh home tasks/recent cases
    },
  });
}

/* ─────────────────────────────────────────────
 * 5. Create contact mutation
 * ───────────────────────────────────────────── */

export function useCreateContact() {
  return useMutation<CreateContactResponse, Error, CreateContactPayload>({
    mutationFn: async (payload) => {
      const { data } = await api.post('/api/contacts', payload);
      return data as CreateContactResponse;
    },
  });
}
